import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Zap, Map, Package, LogOut, Crown, Sparkles, Shield, FileText } from 'lucide-react';

import ComponentInventory from './components/ComponentInventory';
import ProgressTree from './components/ProgressTree';
import ChallengePage from './components/ChallengePage';
import ProfilePage from './components/ProfilePage';
import DailyStreak from './components/DailyStreak';
import Leaderboard from './components/Leaderboard';
import ComponentCrafting from './components/ComponentCrafting';
import Achievements from './components/Achievements';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import { VerifyEmailNoticePage, VerifyEmailTokenPage } from './pages/VerifyEmailPage';
import AdminApp from './admin/AdminApp';
import PolicyPages from './pages/PolicyPages';
import PricingPage from './pages/PricingPage';
import PublicChallengePage from './pages/PublicChallengePage';
import LandingPage from './pages/LandingPage';
import MySubmissionsPage from './pages/MySubmissionsPage';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import { Analytics } from '@vercel/analytics/react';
import useUserStore from './store/userStore';
import useAdminStore from './store/adminStore';

// Level system
const LEVELS = [
  { level: 1, name: 'Novice', minDP: 0 },
  { level: 2, name: 'Apprentice', minDP: 100 },
  { level: 3, name: 'Junior Architect', minDP: 250 },
  { level: 4, name: 'Architect', minDP: 500 },
  { level: 5, name: 'Senior Architect', minDP: 1000 },
  { level: 6, name: 'Lead Architect', minDP: 2000 },
  { level: 7, name: 'Principal Architect', minDP: 4000 },
  { level: 8, name: 'Staff Architect', minDP: 7500 },
  { level: 9, name: 'Distinguished Architect', minDP: 12500 },
  { level: 10, name: 'Master Architect', minDP: 20000 },
];

const getUserLevel = (dp) => {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (dp >= LEVELS[i].minDP) return LEVELS[i];
  }
  return LEVELS[0];
};

const Navbar = () => {
  const { user, isAuthenticated, logout } = useUserStore();
  const { isAdmin, checkAdminAccess } = useAdminStore();
  const levelInfo = user ? getUserLevel(user.design_points + (user.total_score || 0)) : null;

  useEffect(() => {
    if (isAuthenticated && isAdmin === null) {
      checkAdminAccess();
    }
  }, [isAuthenticated]);

  return (
    <nav className="bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="relative">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="text-white font-bold text-lg">B</span>
              </div>
              <Sparkles className="absolute -top-1 -right-1 w-4 h-4 text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <span className="text-xl font-bold text-white">
              buildex.dev
            </span>
          </Link>

          {isAuthenticated && user && (
            <div className="flex items-center gap-6">
              <Link
                to="/challenges"
                className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors group"
              >
                <Map className="w-5 h-5 group-hover:animate-pulse" />
                <span className="font-medium">Challenge Hub</span>
              </Link>
              <Link
                to="/inventory"
                className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors group"
              >
                <Package className="w-5 h-5 group-hover:animate-pulse" />
                <span className="font-medium">Inventory</span>
              </Link>
              <Link
                to="/submissions"
                className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors group"
              >
                <FileText className="w-5 h-5 group-hover:animate-pulse" />
                <span className="font-medium">Submissions</span>
              </Link>

              <div className="flex items-center gap-4 pl-4 border-l border-slate-700">
                {/* Level badge */}
                <Link to="/profile" className="flex items-center gap-3 group">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold group-hover:scale-110 transition-transform">
                      {user.username?.charAt(0).toUpperCase()}
                    </div>
                    <div className="absolute -bottom-1 -right-1 px-1.5 py-0.5 bg-yellow-400 rounded-full text-xs font-bold text-yellow-900 shadow-sm">
                      {levelInfo?.level || 1}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-white flex items-center gap-1">
                      {user.username}
                      {levelInfo?.level >= 5 && <Crown className="w-4 h-4 text-yellow-500" />}
                    </div>
                    <div className="text-xs text-slate-400">{levelInfo?.name}</div>
                  </div>
                </Link>

                {/* DP display */}
                <div className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-full">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-bold text-yellow-300">{user.design_points} DP</span>
                </div>

                {/* Premium badge or upgrade button */}
                {user.is_premium ? (
                  <div className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full text-white">
                    <Crown className="w-4 h-4" />
                    <span className="text-sm font-bold">PRO</span>
                  </div>
                ) : (
                  <Link
                    to="/pricing"
                    className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>Upgrade</span>
                  </Link>
                )}

                {/* Admin button - only for admins */}
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="p-2 text-slate-400 hover:text-blue-400 hover:bg-slate-800 rounded-lg transition-all"
                    title="Admin Panel"
                  >
                    <Shield className="w-5 h-5" />
                  </Link>
                )}

                <button
                  onClick={logout}
                  className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-all"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

const HomePage = () => {
  const { user, stats, fetchProfile } = useUserStore();

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, []);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-2xl p-12 mb-8 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl blur-3xl"></div>
          <div className="relative">
            <h1 className="text-4xl font-bold mb-4 text-white">
              Welcome back, {user?.username}!
            </h1>
            <p className="text-xl mb-6 text-slate-300">
              Master system design through gamified challenges. Earn Design Points,
              unlock components, and conquer all challenges!
            </p>
            <div className="flex gap-4">
              <Link
                to="/challenges"
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all"
              >
                Continue Journey
              </Link>
              <Link
                to="/inventory"
                className="px-6 py-3 bg-slate-800 text-white rounded-xl font-semibold border border-slate-700 hover:bg-slate-700 transition-colors"
              >
                View Inventory
              </Link>
            </div>
          </div>
        </div>

        {/* Quick Guide */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
          <h2 className="text-2xl font-bold mb-6 text-white">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Choose a Challenge</h3>
              <p className="text-slate-400 text-sm">
                Navigate the quest map and select an unlocked system design
                challenge that matches your skill level.
              </p>
            </div>

            <div>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Design Your System</h3>
              <p className="text-slate-400 text-sm">
                Use your unlocked components to build a scalable architecture.
                Make strategic decisions about databases, caching, and more.
              </p>
            </div>

            <div>
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Earn Rewards</h3>
              <p className="text-slate-400 text-sm">
                Get scored on efficiency, cost, and reliability. Earn Design
                Points to unlock advanced components and new challenges!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ChallengeHubPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useUserStore();
  const [showSignUpModal, setShowSignUpModal] = useState(false);

  const handleChallengeSelect = (challenge) => {
    if (isAuthenticated) {
      // Authenticated users go directly to the challenge
      navigate(`/challenge/${challenge.id}`);
    } else {
      // Non-authenticated users
      if (challenge.is_lead_magnet) {
        // Lead magnet challenge goes to /try
        navigate('/try');
      } else if (challenge.is_free) {
        // Free challenges go to /trial/:id
        navigate(`/trial/${challenge.id}`);
      } else {
        // Other challenges show sign-up modal
        setShowSignUpModal(true);
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Show navbar for non-authenticated users */}
      {!isAuthenticated && (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2 group">
                <div className="relative">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <span className="text-white font-bold text-lg">B</span>
                  </div>
                  <Sparkles className="absolute -top-1 -right-1 w-4 h-4 text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <span className="text-xl font-bold text-white">buildex.dev</span>
              </Link>
              <div className="flex items-center gap-3">
                <Link to="/login" className="px-4 py-2 text-slate-300 hover:text-white font-medium">
                  Sign In
                </Link>
                <Link to="/register" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
                  Sign Up Free
                </Link>
              </div>
            </div>
          </div>
        </nav>
      )}

      <div className={!isAuthenticated ? 'pt-20' : ''}>
        <ProgressTree onChallengeSelect={handleChallengeSelect} isPublicView={!isAuthenticated} />
      </div>

      {/* Sign Up Modal for non-authenticated users */}
      {showSignUpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowSignUpModal(false)} />
          <div className="relative bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-slate-700">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8 text-white text-center">
              <Sparkles className="w-12 h-12 mx-auto mb-3" />
              <h2 className="text-2xl font-bold mb-2">Sign Up to Play</h2>
              <p className="text-indigo-100">Create a free account to access this challenge and track your progress!</p>
            </div>
            <button
              onClick={() => setShowSignUpModal(false)}
              className="absolute top-4 right-4 text-white/80 hover:text-white"
            >
              <span className="text-2xl">&times;</span>
            </button>
            <div className="p-6">
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/register')}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                >
                  Sign Up Free
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 border-slate-600 text-slate-300 rounded-lg font-semibold hover:bg-slate-700 transition-colors"
                >
                  Already have an account? Sign In
                </button>
              </div>
              <div className="mt-4 text-center">
                <button
                  onClick={() => { setShowSignUpModal(false); navigate('/try'); }}
                  className="text-indigo-400 hover:text-indigo-300 text-sm"
                >
                  Or try our free challenge first →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InventoryPage = () => {
  return (
    <div className="container mx-auto px-4 py-8">
      <ComponentInventory />
    </div>
  );
};

const ProfilePageWrapper = () => {
  return (
    <div className="container mx-auto px-4 py-8">
      <ProfilePage />
    </div>
  );
};

const AchievementsPage = () => {
  return (
    <div className="container mx-auto px-4 py-8">
      <Achievements />
    </div>
  );
};

const StreakPage = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <DailyStreak />
    </div>
  );
};

const LeaderboardPage = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Leaderboard />
    </div>
  );
};

const WorkshopPage = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <ComponentCrafting />
    </div>
  );
};


function App() {
  const { isAuthenticated } = useUserStore();

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col">
        {isAuthenticated && <Navbar />}

        <main className="flex-1 flex flex-col">
        <Routes>
          {/* Admin routes */}
          <Route path="/admin/*" element={<AdminApp />} />

          {/* Auth routes */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/" /> : <LoginPage />}
          />
          <Route
            path="/register"
            element={isAuthenticated ? <Navigate to="/" /> : <RegisterPage />}
          />
          <Route
            path="/verify-email"
            element={<VerifyEmailTokenPage />}
          />
          <Route
            path="/verify-email-notice"
            element={isAuthenticated ? <VerifyEmailNoticePage /> : <Navigate to="/login" />}
          />

          {/* Public policy pages */}
          <Route path="/policy/:page" element={<PolicyPages />} />

          {/* Lead magnet - free trial challenge */}
          <Route path="/try" element={<PublicChallengePage />} />

          {/* Trial page for any challenge (shareable URLs) */}
          <Route path="/trial/:challengeId" element={<PublicChallengePage />} />

          {/* Home - Landing page for non-auth, Dashboard for auth */}
          <Route
            path="/"
            element={isAuthenticated ? <HomePage /> : <LandingPage />}
          />
          <Route
            path="/challenges"
            element={<ChallengeHubPage />}
          />
          <Route
            path="/inventory"
            element={isAuthenticated ? <InventoryPage /> : <Navigate to="/login" />}
          />
          <Route
            path="/challenge/:id"
            element={isAuthenticated ? <ChallengePage /> : <Navigate to="/login" />}
          />
          <Route
            path="/profile"
            element={isAuthenticated ? <ProfilePageWrapper /> : <Navigate to="/login" />}
          />
          <Route
            path="/achievements"
            element={isAuthenticated ? <AchievementsPage /> : <Navigate to="/login" />}
          />
          <Route
            path="/streak"
            element={isAuthenticated ? <StreakPage /> : <Navigate to="/login" />}
          />
          <Route
            path="/leaderboard"
            element={isAuthenticated ? <LeaderboardPage /> : <Navigate to="/login" />}
          />
          <Route
            path="/submissions"
            element={isAuthenticated ? <MySubmissionsPage /> : <Navigate to="/login" />}
          />
          <Route
            path="/workshop"
            element={isAuthenticated ? <WorkshopPage /> : <Navigate to="/login" />}
          />
          <Route
            path="/pricing"
            element={isAuthenticated ? <PricingPage /> : <Navigate to="/login" />}
          />
        </Routes>
        </main>

        {/* Only show shared Footer for authenticated pages - LandingPage has its own footer */}
        {isAuthenticated && <Footer />}

        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />

        {/* Cookie Consent Banner - GDPR/CCPA/DPDP compliant */}
        <CookieConsent />

        {/* Vercel Analytics */}
        <Analytics />
      </div>
    </Router>
  );
}

export default App;
